import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchAvatar, fetchAvatars, createAvatar, updateAvatar, reorderAvatars } from "src/actions/mine_craft/avatarsActions";

export interface AvatarInterface {
  url: string;

  id: number;
  app_id: number;

  user_id?: number | null;
  user?: number | null;

  image_url: string;
  order?: number;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: AvatarInterface | undefined;
  newError: string | null;

  newGroupParams: AvatarInterface | undefined;
  newGroupError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  newGroupParams: undefined,
  newGroupError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<AvatarInterface>();

const slice = createSlice({
  name: 'avatars',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      const defaults = {}

      state.newParams = action.payload ? { ...defaults, ...action.payload } : undefined;
      state.newError  = initialState.newError;
    },
    setNewGroupParams: (state, action) => {
      state.newGroupParams = action.payload;
      state.newGroupError  = initialState.newGroupError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setReorderIds: (state, action) => {
      state.reorderIds = action.payload.ids;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchAvatars.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchAvatars.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.avatars;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchAvatars.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createAvatar.fulfilled, (state, action) => {
        const avatar = Object.values(action.payload.avatar)[0];
        state.entities[avatar.id] = avatar;
      })
      .addCase(createAvatar.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchAvatar.fulfilled, (state, action) => {
        const avatar = Object.values(action.payload.avatar)[0];
        state.entities[avatar.id] = avatar;
      })

      // #update
      .addCase(updateAvatar.fulfilled, (state, action) => {
        const avatar = Object.values(action.payload.avatar)[0];
        state.entities[avatar.id] = avatar;
      })
      .addCase(updateAvatar.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderAvatars.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewGroupParams,
  setNewError,
  setEditError,
  setReorderIds
} = slice.actions;

export const {
  selectById: selectAvatarById,
  selectIds: selectAvatarIds,
  selectEntities: selectAvatarEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.avatars);
